import { useMutation } from "@apollo/client";
import { toast } from "sonner";
import { AppointmentDetailFragment, UpdateAppointmentInput } from "@/gql/graphql";
import {
  TODAYS_APPOINTMENTS_CURSOR_QUERY,
  UPDATE_APPOINTMENT_MUTATION,
  WAITING_TO_CONFIRM_APPOINTMENTS_QUERY,
} from "@/graphql/appointments";
import { handleGraphQLErrors } from "@/lib/error/apollo-utils";
import { useAsyncAction } from "@/lib/hooks/use-async-action";
import { useSalonStore } from "@/lib/store/salon";
import { cleanUpdateInput } from "../utils/appointment-utils";
import { sendWithOverCapacityApproval } from "../utils/over-capacity-approval";

export interface UseUpdateAppointmentReturn {
  updateAppointment: (
    id: string,
    updates: Partial<UpdateAppointmentInput>
  ) => Promise<AppointmentDetailFragment>;
  isUpdating: boolean;
  error: Error | null;
  reset: () => void;
}

export const useUpdateAppointment = (): UseUpdateAppointmentReturn => {
  const currentSalon = useSalonStore((state) => state.currentSalon);

  const [updateAppointmentMutation] = useMutation(UPDATE_APPOINTMENT_MUTATION, {
    errorPolicy: "all",
    refetchQueries: [TODAYS_APPOINTMENTS_CURSOR_QUERY, WAITING_TO_CONFIRM_APPOINTMENTS_QUERY],
  });

  const { execute, isLoading, error, reset } = useAsyncAction(
    async (appointmentId: string, updates: Partial<UpdateAppointmentInput>) => {
      if (!currentSalon?.id) {
        throw new Error("No salon selected");
      }
      const salonId = currentSalon.id;

      const result = await sendWithOverCapacityApproval((acknowledgement) =>
        updateAppointmentMutation({
          variables: {
            salonId,
            appointmentId,
            input: cleanUpdateInput({ ...updates, ...acknowledgement }),
          },
        })
      );
      if (!result) {
        return null;
      }

      if (result.errors) {
        handleGraphQLErrors(result.errors);
      }
      toast.success("Appointment updated successfully");
      return result.data?.updateAppointment;
    },
    {
      loadingMessage: "Updating appointment...",
      showSuccessToast: false,
      errorMessage: "Failed to update appointment",
    }
  );

  return {
    updateAppointment: execute as (
      id: string,
      updates: Partial<UpdateAppointmentInput>
    ) => Promise<AppointmentDetailFragment>,
    isUpdating: isLoading,
    error,
    reset,
  };
};
